import { memo, useCallback, useMemo, useState } from "react";
import { Pressable } from "react-native";
import { useQueries } from "@tanstack/react-query";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { AlarmStateBadge } from "./AlarmStateBadge";
import { prunePastExceptions } from "@src/features/alarms/utils/alarm-utils";
import { toLocalDate, makeGroupKey } from "@src/features/alarms/utils/exception-format";
import { isExceptionAllDayRange } from "@src/features/schedules/utils/schedule-utils";
import { api } from "@src/services/api/client";
import { useTenantId } from "@src/features/tenants";
import type { AlarmException, AlarmAction } from "@src/services/api/types";
import type { FC } from "react";
import Animated, { FadeIn, FadeOut, LinearTransition } from "react-native-reanimated";
import { isWeb } from "@src/utils/web-helpers";

type AggregatedExceptionGroup = {
  key: string;
  reason: string;
  action: AlarmAction;
  period: AlarmException["period"];
  cameraCount: number;
  cameraKeys: string[];
  timezone: string;
};

type MonthGroup = {
  label: string;
  items: AggregatedExceptionGroup[];
};

const LayoutAnimation = isWeb ? undefined : LinearTransition.springify();

const formatTime = (date: Date, tz: string): string =>
  new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz }).format(date).toLowerCase();

const isSameDay = (date: Date, tz: string): boolean => {
  const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: tz });
  return fmt.format(date) === fmt.format(new Date());
};

type UpcomingExceptionsProps = {
  siteId: string;
  cameraKeys: string[];
};

export const UpcomingExceptions: FC<UpcomingExceptionsProps> = memo(({ siteId, cameraKeys }) => {
  const tenantId = useTenantId();
  const totalCameras = cameraKeys.length;
  const { theme } = useUnistyles();
  const alarmQueries = useQueries({
    queries: cameraKeys.map((cameraKey) => ({
      queryKey: ["alarms", tenantId, siteId, cameraKey],
      queryFn: () => api.alarms.getByCamera(tenantId, siteId, cameraKey),
      enabled: !!tenantId && !!siteId && !!cameraKey,
      staleTime: 60_000,
    })),
  });

  const aggregated = useMemo(() => {
    const now = new Date();
    const groups = new Map<string, AggregatedExceptionGroup>();

    for (let i = 0; i < alarmQueries.length; i++) {
      const config = alarmQueries[i].data;
      if (!config?.settings) continue;
      const cameraKey = cameraKeys[i];
      const tz = config.settings.timezone ?? "UTC";
      const upcoming = prunePastExceptions(
        config.settings.exceptions.filter((e) => e.period?.start && e.period?.end),
        now,
        tz,
      );
      for (const ex of upcoming) {
        const key = makeGroupKey(ex);
        const existing = groups.get(key);
        if (existing) {
          existing.cameraCount += 1;
          existing.cameraKeys.push(cameraKey);
        } else {
          groups.set(key, {
            key,
            reason: ex.reason,
            action: ex.action,
            period: ex.period,
            cameraCount: 1,
            cameraKeys: [cameraKey],
            timezone: tz,
          });
        }
      }
    }

    return [...groups.values()].sort((a, b) => {
      const aDate = toLocalDate(a.period.start, a.timezone);
      const bDate = toLocalDate(b.period.start, b.timezone);
      return aDate.getTime() - bDate.getTime();
    });
  }, [alarmQueries, cameraKeys]);

  const monthGroups = useMemo((): MonthGroup[] => {
    const result: MonthGroup[] = [];

    for (const item of aggregated) {
      const date = toLocalDate(item.period.start, item.timezone);
      const label = new Intl.DateTimeFormat("en-US", {
        month: "long",
        year: "numeric",
        timeZone: item.timezone,
      }).format(date);
      const last = result[result.length - 1];
      if (last && last.label === label) {
        last.items.push(item);
      } else {
        result.push({ label, items: [item] });
      }
    }
    return result;
  }, [aggregated]);

  if (monthGroups.length === 0 && isWeb) {
    return (
      <View gap="3xl">
        <Text textStyle="md/medium">No upcoming exceptions</Text>
        <Text textStyle="sm/regular" color="textMuted">
          Exceptions allow you to make temporary changes to your routine, such as arming the site all day on key dates.
        </Text>
      </View>
    );
  }
  if (monthGroups.length === 0) return null;

  return (
    <View gap="3xl">
      <Text textStyle="md/medium">Upcoming exceptions</Text>
      {monthGroups.map((month) => (
        <Animated.View key={month.label} layout={LayoutAnimation}>
          <Animated.View layout={LayoutAnimation} style={{ paddingLeft: theme.spacing["9xl"] }}>
            <Text textStyle="xs/medium" color="textMuted">
              {month.label}
            </Text>
          </Animated.View>
          {month.items.map((item) => (
            <ExceptionRow key={item.key} item={item} totalCameras={totalCameras} />
          ))}
        </Animated.View>
      ))}
    </View>
  );
});

UpcomingExceptions.displayName = "UpcomingExceptions";

const ExceptionRow: FC<{ item: AggregatedExceptionGroup; totalCameras: number }> = memo(({ item, totalCameras }) => {
  const { theme } = useUnistyles();
  const [expanded, setExpanded] = useState(false);
  const startDate = toLocalDate(item.period.start, item.timezone);
  const tz = item.timezone;
  const allDay = isExceptionAllDayRange(item.period.start, item.period.end);
  const today = isSameDay(startDate, tz);
  const dayAbbr = new Intl.DateTimeFormat("en-US", { weekday: "narrow", timeZone: tz }).format(startDate);
  const dayNum = new Intl.DateTimeFormat("en-US", { day: "2-digit", timeZone: tz }).format(startDate);

  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const timeLabel = useMemo(() => {
    if (allDay) return "All day";
    const start = formatTime(startDate, tz);
    const end = formatTime(toLocalDate(item.period.end, tz), tz);
    return `${start} - ${end}`;
  }, [allDay, startDate, item.period.end, tz]);

  return (
    <Animated.View layout={LayoutAnimation} style={styles.row}>
      <View style={styles.dateColumn}>
        <Text
          textStyle="sm/medium"
          textAlign="center"
          style={{ color: today ? theme.colors.interactive.buttons.default_background : theme.colors.textMuted }}
        >
          {dayAbbr}
        </Text>
        <View style={[styles.containerNumber, today && styles.todayContainerNumber]}>
          <Text textStyle="sm/semibold" textAlign="center" color={today ? "white" : "text"}>
            {dayNum}
          </Text>
        </View>
      </View>
      <Animated.View layout={LayoutAnimation} style={styles.content}>
        <View flexDirection="row" alignItems="center" justifyContent="space-between">
          <View
            flexDirection="row"
            alignItems="center"
            gap="xs"
            style={{ flexShrink: 1, paddingRight: theme.spacing.lg }}
          >
            <Text textStyle="xs/medium" numberOfLines={1} style={{ flexShrink: 1 }}>
              {item.reason}
            </Text>
            <Text textStyle="xs/regular" color="textMuted">
              &bull;
            </Text>
            <Icon name="clockOutline" size={14} color={theme.colors.textMuted} />
            <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
              {timeLabel}
            </Text>
          </View>
          <AlarmStateBadge state={item.action} />
        </View>
        <View h={1} bg="borderCard" />
        <Pressable onPress={handleToggle}>
          <View flexDirection="row" alignItems="center" justifyContent="space-between">
            <View flexDirection="row" alignItems="center" gap="xs">
              <Icon name="videoOutline" size={14} color={theme.colors.textMuted} />
              <Text textStyle="xs/regular" color="textMuted">
                {item.cameraCount} of {totalCameras} cameras
              </Text>
            </View>
            <Icon name={expanded ? "chevronUp" : "chevronDown"} size={20} color={theme.colors.textMuted} />
          </View>
        </Pressable>
        {expanded && (
          <Animated.View
            entering={FadeIn.duration(400)}
            exiting={FadeOut.duration(100)}
            layout={LayoutAnimation}
            style={styles.cameraChips}
          >
            {item.cameraKeys.map((key) => (
              <View key={key} style={styles.chip}>
                <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                  {key}
                </Text>
              </View>
            ))}
          </Animated.View>
        )}
      </Animated.View>
    </Animated.View>
  );
});

ExceptionRow.displayName = "ExceptionRow";

const styles = StyleSheet.create((theme) => ({
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xl,
    paddingVertical: theme.spacing["3xl"],
    borderBottomWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  dateColumn: {
    width: 24,
    alignSelf: "flex-start",
    flexDirection: "column",
  },
  content: {
    flex: 1,
    gap: theme.spacing.md,
    padding: theme.spacing.lg,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    backgroundColor: theme.colors.surfaceCard,
  },
  cameraChips: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing.xs,
  },
  chip: {
    borderWidth: 1.5,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.interactive.secondary.background_secondary,
  },
  containerNumber: {
    height: 24,
    aspectRatio: 1,
    justifyContent: "center",
    alignItems: "center",
    marginTop: theme.spacing.xs,
  },
  todayContainerNumber: {
    backgroundColor: theme.colors.interactive.buttons.default_background,
    borderRadius: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
  },
}));
